import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Zoom dialog:
 * - Desktop: vertical scroll-snap through all images, IntersectionObserver tracks active image
 * - Mobile: single-image lightbox with prev/next buttons + drag-zoom-wrapper for pinch
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  /** @type {number} */
  #activeIndex = 0;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {number} */
  #wheelAccumulator = 0;
  /** @type {boolean} */
  #wheelNavigating = false;

  get #isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('wheel', this.#handleWheel, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('wheel', this.#handleWheel);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** Intercepts wheel events so scroll-lock can't block them, navigates between images. */
  #handleWheel = (/** @type {WheelEvent} */ event) => {
    if (!this.#isDesktop) return;
    event.preventDefault();
    event.stopPropagation();

    if (this.#wheelNavigating) return;

    this.#wheelAccumulator += event.deltaY;

    if (this.#wheelAccumulator > 40) {
      this.#wheelAccumulator = 0;
      this.#wheelNavigating = true;
      this.#scrollToImage(this.#activeIndex + 1);
      setTimeout(() => { this.#wheelNavigating = false; }, 700);
    } else if (this.#wheelAccumulator < -40) {
      this.#wheelAccumulator = 0;
      this.#wheelNavigating = true;
      this.#scrollToImage(this.#activeIndex - 1);
      setTimeout(() => { this.#wheelNavigating = false; }, 700);
    }
  };

  // ─── Desktop: IntersectionObserver for scroll tracking ──────────────────────

  #setupObserver() {
    this.#observer?.disconnect();

    const gallery = this.querySelector('.dialog-zoomed-gallery');
    if (!gallery) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        let best = null;
        let bestRatio = 0;
        for (const entry of entries) {
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            best = entry.target;
          }
        }
        if (!best || bestRatio < 0.4) return;
        const index = this.refs.media.indexOf(/** @type {HTMLElement} */ (best));
        if (index === -1 || index === this.#activeIndex) return;

        this.#activeIndex = index;
        this.#syncThumbnails(index);
        this.dispatchEvent(new ZoomMediaSelectedEvent(index));
        this.#loadHighResolutionImage(this.refs.media[index]);
        if (this.refs.media[index + 1]) this.#loadHighResolutionImage(this.refs.media[index + 1]);
      },
      { root: gallery, threshold: [0, 0.25, 0.5, 0.75, 1.0] }
    );

    this.refs.media.forEach((el) => this.#observer.observe(el));
  }

  #scrollToImage(index, behavior = 'smooth') {
    const { media } = this.refs;
    const target = media[Math.max(0, Math.min(index, media.length - 1))];
    if (target) target.scrollIntoView({ behavior, block: 'start' });
  }

  // ─── Mobile: single-image mode with hidden attribute ───────────────────────

  #showImage(index, behavior = 'smooth') {
    const { media } = this.refs;
    this.#activeIndex = Math.max(0, Math.min(index, media.length - 1));

    media.forEach((el, i) => {
      if (i === this.#activeIndex) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });

    this.#syncThumbnails(this.#activeIndex);
    this.#loadHighResolutionImage(media[this.#activeIndex]);
    if (media[this.#activeIndex + 1]) this.#loadHighResolutionImage(media[this.#activeIndex + 1]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#activeIndex));
    this.#updateMobileNavButtons();
  }

  #updateMobileNavButtons() {
    const { media } = this.refs;
    const prevBtn = this.querySelector('[data-zoom-prev]');
    const nextBtn = this.querySelector('[data-zoom-next]');
    if (prevBtn instanceof HTMLElement) prevBtn.hidden = this.#activeIndex <= 0;
    if (nextBtn instanceof HTMLElement) nextBtn.hidden = this.#activeIndex >= media.length - 1;
  }

  // ─── Shared ────────────────────────────────────────────────────────────────

  #syncThumbnails(index) {
    const { thumbnails } = this.refs;
    Array.from(thumbnails.querySelectorAll('button')).forEach((btn, i) => {
      btn.setAttribute('aria-selected', `${i === index}`);
    });
    const activeThumbnail = thumbnails.children[index];
    if (activeThumbnail instanceof HTMLElement) {
      scrollIntoView(activeThumbnail, {
        ancestor: thumbnails,
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    const open = () => {
      this.#activeIndex = index;
      this.#syncThumbnails(index);
      this.#loadHighResolutionImage(targetImage);

      if (this.#isDesktop) {
        media.forEach((el) => el.removeAttribute('hidden'));
        dialog.showModal();
        requestAnimationFrame(() => {
          targetImage?.scrollIntoView({ behavior: 'instant', block: 'start' });
          this.#setupObserver();
        });
      } else {
        this.#showImage(index, 'instant');
        dialog.showModal();
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage =
      event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage)
      return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');
  }

  /** @param {KeyboardEvent} event */
  handleKeyDown(event) {
    const desktop = this.#isDesktop;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (desktop) this.#scrollToImage(this.#activeIndex + 1);
        else this.#showImage(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (desktop) this.#scrollToImage(this.#activeIndex - 1);
        else this.#showImage(this.#activeIndex - 1);
        break;
      case 'ArrowRight':
        if (!desktop) { event.preventDefault(); this.#showImage(this.#activeIndex + 1); }
        break;
      case 'ArrowLeft':
        if (!desktop) { event.preventDefault(); this.#showImage(this.#activeIndex - 1); }
        break;
    }
  }

  /** Mobile prev button */
  handlePrev() {
    this.#showImage(this.#activeIndex - 1);
  }

  /** Mobile next button */
  handleNext() {
    this.#showImage(this.#activeIndex + 1);
  }

  /** @param {number} index */
  async handleThumbnailClick(index) {
    if (this.#isDesktop) this.#scrollToImage(index);
    else this.#showImage(index);
  }

  /** @param {number} index */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (media[index]) this.#loadHighResolutionImage(media[index]);
  }

  /**
   * @param {number} index
   * @param {{ behavior: ScrollBehavior }} [options]
   */
  selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (this.#isDesktop) this.#scrollToImage(index, options.behavior);
    else this.#showImage(index, options.behavior);
  }

  /** @param {HTMLElement} mediaContainer */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeElement = media[this.#activeIndex];
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[this.#activeIndex]
      : mediaGallery?.media?.[this.#activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.thumbnails);

    activeElement?.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      activeElement?.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
